import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, UserProfile } from '../services/api';

export const useAuth = () => {
  // Initialize with token check to avoid false negative
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('adminToken');
 
    return !!token;
  });
  const queryClient = useQueryClient();

  // Check authentication status on mount and storage changes
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("adminToken");
  
      setIsAuthenticated(!!token);
    };

    // Check on mount
    checkAuth();

    // Listen for storage changes (e.g., token removed by axios interceptor)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "adminToken") {
      
        checkAuth();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also check periodically to catch same-tab changes
    const interval = setInterval(checkAuth, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Fetch user profile when authenticated
  const { 
    data: profile, 
    isLoading: isLoadingProfile, 
    error: profileError 
  } = useQuery<UserProfile>({
    queryKey: ['userProfile'],
    queryFn: adminApi.getProfile,
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Handle authentication errors
  useEffect(() => {
    if (profileError) {
      const error = profileError as any;
  

      // Check if token still exists but profile failed
      const token = localStorage.getItem("adminToken");

      if (error?.response?.status === 401 || !token) {
       
        setIsAuthenticated(false);
        queryClient.clear();

        // The axios interceptor will handle the redirect, but we ensure state is cleared
        if (token) {
          localStorage.removeItem("adminToken");
        }
      }
    }
  }, [profileError, queryClient]);


  // Login function
  const login = (token: string) => {
 
    localStorage.setItem('adminToken', token);
  
    setIsAuthenticated(true);
   
    // Invalidate and refetch profile
    queryClient.invalidateQueries({ queryKey: ['userProfile'] });
  
  };

  // Logout function
  const logout = () => {
    adminApi.logout();
    setIsAuthenticated(false);
    // Clear all queries
    queryClient.clear();
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (isLoadingProfile) return 'Loading...';
    if (profileError) return 'Error loading profile';
    
    const user = profile?.data?.admin || profile?.user;
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.name || user?.email?.split('@')[0] || 'User Name';
  };

  // Get user email
  const getUserEmail = () => {
    if (isLoadingProfile) return 'Loading...';
    if (profileError) return 'Check console for details';
    
    const user = profile?.data?.admin || profile?.user;
    return user?.email || 'user@example.com';
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    const user = profile?.data?.admin || profile?.user;
    if (user?.firstName?.charAt(0) && user?.lastName?.charAt(0)) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
    }
    if (user?.name?.charAt(0)) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.email?.charAt(0)) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'UN';
  };

  return {
    // State
    isAuthenticated,
    profile,
    isLoadingProfile,
    profileError,

    // Actions
    login,
    logout,

    // Computed values
    getUserDisplayName,
    getUserEmail,
    getUserInitials,
  };
};
